import api from "./api";
import { Service } from "@/types";

/**
 * Response shape for GET /services after envelope unwrap.
 * API returns: { services: [...], total, page, limit }
 */
interface ServicesResponse {
  services: Service[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Query parameters supported by the GET /services endpoint.
 */
interface ServiceQueryParams {
  page?: number;
  limit?: number;
}

/**
 * Service layer for Services API endpoints.
 * Base path: /api/v1/services
 */
export const serviceService = {
  /**
   * Fetch all services with optional pagination.
   * API returns: { services: [...], total, page, limit }
   * @param params - Optional pagination params
   * @returns Array of Service items
   */
  getServices: async (params?: ServiceQueryParams): Promise<Service[]> => {
    const raw: any = await api.get("/services", {
      params,
    });

    // Handle the standard envelope { status, data: { docs, meta } }
    // Or the unwrapped data from the 'api' utility if it already unwraps
    const data = raw.data || raw;
    const docs = Array.isArray(data.docs) 
      ? data.docs 
      : Array.isArray(data) 
        ? data 
        : [];

    return docs.map((item: any) => ({
      ...item,
      id: item._id || item.id,
      title: item.title || item.name, // Ensure UI can find the title
    }));
  },

  /**
   * Fetch a specific service by its ID.
   * @param id - The service ID
   */
  getServiceById: async (id: string): Promise<Service> => {
    const raw: Record<string, unknown> = await api.get(`/services/${id}`);
    const service = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(service as unknown as Service),
      id: (service._id as string) || (service.id as string),
    };
  },

  /**
   * Fetch a specific service by its slug.
   * @param slug - The service slug
   */
  getServiceBySlug: async (slug: string): Promise<Service> => {
    const raw: Record<string, unknown> = await api.get(
      `/services/${slug}`,
    );
    const service = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(service as unknown as Service),
      id: (service._id as string) || (service.id as string),
    };
  },

  /**
   * Create a new service.
   * @param data - The service data
   */
  createService: async (data: Partial<Service>): Promise<Service> => {
    const raw: Record<string, unknown> = await api.post("/services", data);
    const service = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(service as unknown as Service),
      id: (service._id as string) || (service.id as string),
    };
  },

  /**
   * Update an existing service.
   * @param id - The service ID
   * @param data - The update data
   */
  updateService: async (
    id: string,
    data: Partial<Service>,
  ): Promise<Service> => {
    const raw: Record<string, unknown> = await api.patch(
      `/services/${id}`,
      data,
    );
    const service = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(service as unknown as Service),
      id: (service._id as string) || (service.id as string),
    };
  },

  /**
   * Delete a service.
   * @param id - The service ID
   */
  deleteService: async (id: string): Promise<void> => {
    await api.delete(`/services/${id}`);
  },

  /**
   * Bulk update the status of multiple services.
   * @param ids - Array of service IDs
   * @param status - 'active' or 'inactive'
   */
  bulkUpdateStatus: async (ids: string[], status: 'active' | 'inactive'): Promise<void> => {
    await api.patch("/services/bulk-status", { ids, status });
  },

  /**
   * Reorder services for display.
   * @param orders - Array of { id, newOrder } objects
   */
  reorderServices: async (orders: { id: string; newOrder: number }[]): Promise<void> => {
    await api.patch("/services/reorder", { orders });
  },

  /**
   * Fetch ecosystem statistics for the dashboard.
   */
  getEcosystemStats: async (): Promise<{ totalServices: number; activeServices: number }> => {
    const raw: any = await api.get("/services/stats");
    const doc = raw.doc || raw.data?.doc || raw.data || raw;
    return {
      totalServices: doc.totalServices || 0,
      activeServices: doc.activeServices || 0,
    };
  },
};
